import { useEffect, useState } from "react";
import styles from "./MyInfo.module.css";

import InfoHeader from "../../components/InfoHeader";
import TabBar from "../../components/TabBar";
import DateHeader from "../../components/DateHeader";
import EventTicketsInfoList from "../../components/EventTicketsInfoList";
import BadgeSection from "../../components/BadgeSection";
import { NotesModal } from "../../components/NotesModal/NotesModal";
import MyInfoTabs from "../../components/MyInfoTabs";
import NameListTab from "../../components/NameListTab";
import InfoTab from "../../components/InfoTab";
import PlatinumBadge from "../../components/BadgeSection/Images/Platnum_Badge.svg";

import Eagle from "./Images/EagleIcon.svg";
import TrendUp from "./Images/TrendUp.svg";
import TrendFlat from "./Images/TrendsFlat.svg";
import TrendDown from "./Images/TrendDown.svg";
import { useHistory, useLocation } from "react-router-dom";
import { useLOSDetailGet } from "./useLOSDetailGet";
import Loader from "components/Loader";

import { useAppDataContext } from "../../App";
import { useTranslation } from "react-i18next";

export const MyInfo = () => {
  const { t } = useTranslation();
  const currentDate = new Date();
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const history = useHistory();
  const location = useLocation();

  const iboNum = location?.state?.iboNum;
  const fromReports = location?.state?.fromReports;
  const fromLOS = location?.state?.fromLOS;
  const iboStack = location?.state?.iboStack || [];
  const tabStateByIbo = location?.state?.tabStateByIbo || {};
  const reportStack = location?.state?.reportStack || [];

  const [fromDetails, setFromDetails] = useState(false);

  useEffect(() => {
    if (iboNum) {
      setFromDetails(true);
    } else {
      setFromDetails(false);
    }
  }, [iboNum]);

  const { iboNumber: appIbo, isLoading: isLoadingIBO } = useAppDataContext();

  const [ibo, setIbo] = useState(iboNum || null);

  useEffect(() => {
    if (!iboNum && appIbo && appIbo !== ibo) {
      setIbo(appIbo);
    }
  }, [iboNum, appIbo, ibo]);

  const [year, setYear] = useState(String(currentDate.getFullYear()));
  const [month, setMonth] = useState(monthNames[currentDate.getMonth()]);
  const [details, setDetails] = useState(null);
  const [activeTab, setActiveTab] = useState("stats");
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);

  const {
    data: detailsData,
    isLoading,
    refetch,
  } = useLOSDetailGet({ year, month, ibo });

  useEffect(() => {
    if (detailsData) {
      setDetails(detailsData);
    }
  }, [detailsData]);

  const onNext = () => {
    const currentMonthIndex = monthNames.indexOf(month);
    if (currentMonthIndex === 11) {
      setMonth(monthNames[0]);
      setYear(String(Number(year) + 1));
    } else {
      setMonth(monthNames[currentMonthIndex + 1]);
    }
  };

  const onBack = () => {
    const currentMonthIndex = monthNames.indexOf(month);
    if (currentMonthIndex === 0) {
      setMonth(monthNames[11]);
      setYear(String(Number(year) - 1));
    } else {
      setMonth(monthNames[currentMonthIndex - 1]);
    }
  };

  const trendColors = {
    up: "#43BA32",
    same: "#FF9A00",
    down: "#ED1F24",
  };

  const pinLevelLabelMap = {
    1: "IBO",
    2: "Platinum",
    3: "Emerald",
    4: "Diamond",
  };

  const pinLevel = pinLevelLabelMap[details?.iboInfo?.loa?.pinLevel] || "";
  const badgeImageUrl =
    details?.iboInfo?.loa?.badgeImageUrl ||
    details?.iboInfo?.loa?.pinBadgeImageUrl ||
    (pinLevel === "Platinum" ? PlatinumBadge : "");
  const hasBadge = Boolean(badgeImageUrl);
  const hasNote = Boolean(
    details?.iboInfo?.loa?.hasNote ?? details?.iboInfo?.loa?.hasPinNote
  );
  const noteDate =
    details?.iboInfo?.loa?.noteDate ||
    details?.iboInfo?.loa?.pinNoteDate ||
    new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date());
  const noteHeadline =
    details?.iboInfo?.loa?.noteHeadline ||
    details?.iboInfo?.loa?.pinNoteHeadline ||
    `${pinLevel || "Current"} Note`;
  const noteBody =
    details?.iboInfo?.loa?.noteBody ||
    details?.iboInfo?.loa?.pinNoteBody ||
    "No note available.";
  const noteAuthor =
    details?.iboInfo?.loa?.noteAuthor ||
    details?.iboInfo?.loa?.pinNoteAuthor ||
    details?.iboInfo?.iboName ||
    "WWG";
  const prospects = details?.nameList?.prospects || details?.prospects || [];
  const customers = details?.nameList?.customers || details?.customers || [];

  const sponsor = {
    name: details?.iboInfo?.loa?.sponsorName || "",
    iboNumber: String(
      details?.iboInfo?.loa?.sponsorIboNumber ??
        details?.iboInfo?.loa?.sponsorIbo ??
        details?.iboInfo?.loa?.sponsorNumber ??
        ""
    ),
  };

  const platinum = {
    name: details?.iboInfo?.loa?.platinumName || "",
    iboNumber: String(
      details?.iboInfo?.loa?.platinumIboNumber ??
        details?.iboInfo?.loa?.platinumIbo ??
        details?.iboInfo?.loa?.platinumNumber ??
        ""
    ),
  };

  const normalizeMember = (member = {}) => ({
    name:
      member.name ||
      member.iboName ||
      member.fullName ||
      member.memberName ||
      "",
    iboNumber: String(
      member.iboNumber ?? member.ibo ?? member.memberIbo ?? member.number ?? ""
    ),
    phone: member.phone || member.phoneNumber || "",
    topicsId: String(
      member.topicsId ??
        member.topicId ??
        member.topicsID ??
        member.topics ??
        ""
    ),
    email: member.email || member.emailAddress || "",
  });

  const memberCandidates =
    details?.iboInfo?.members ||
    details?.iboInfo?.iboMembers ||
    details?.members;

  const syntheticMembers = [
    {
      name: details?.iboInfo?.iboAName || details?.iboInfo?.iboA?.name || "",
      iboNumber:
        details?.iboInfo?.iboA || details?.iboInfo?.iboA?.iboNumber || "",
      phone: details?.iboInfo?.iboAPhone || details?.iboInfo?.iboA?.phone || "",
      topicsId:
        details?.iboInfo?.iboATopicsId ||
        details?.iboInfo?.iboA?.topicsId ||
        "",
      email: details?.iboInfo?.iboAEmail || details?.iboInfo?.iboA?.email || "",
    },
    {
      name: details?.iboInfo?.iboBName || details?.iboInfo?.iboB?.name || "",
      iboNumber:
        details?.iboInfo?.iboB || details?.iboInfo?.iboB?.iboNumber || "",
      phone: details?.iboInfo?.iboBPhone || details?.iboInfo?.iboB?.phone || "",
      topicsId:
        details?.iboInfo?.iboBTopicsId ||
        details?.iboInfo?.iboB?.topicsId ||
        "",
      email: details?.iboInfo?.iboBEmail || details?.iboInfo?.iboB?.email || "",
    },
  ].filter(
    (member) =>
      member.name ||
      member.iboNumber ||
      member.phone ||
      member.topicsId ||
      member.email
  );

  const members = (
    Array.isArray(memberCandidates) && memberCandidates.length
      ? memberCandidates
      : syntheticMembers
  ).map(normalizeMember);

  if (isLoading || !details) {
    return (
      <div className={styles.loaderContainer}>
        <Loader color="#22252e" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <InfoHeader
        pinLevel={details.iboInfo.loa.pinLevel}
        firstName={
          details.iboInfo.iboName
            ? details.iboInfo.iboName.trim().split(" ").slice(0, -1).join(" ")
            : ""
        }
        lastName={
          details.iboInfo.iboName
            ? details.iboInfo.iboName.trim().split(" ").slice(-1)[0]
            : ""
        }
        iboNum={details.iboInfo.ibo}
        avatar={
          details.iboInfo.iboAAvatar
            ? [details.iboInfo.iboAAvatar, details.iboInfo.iboBAvatar]
            : [details.iboInfo.iboAAvatar]
        }
        onLOS={
          fromDetails && fromLOS
            ? () =>
                history.push("/los", {
                  iboNum: ibo,
                  iboStack,
                  showDetailView: true,
                  tabStateByIbo,
                })
            : undefined
        }
        onBack={
          fromDetails && fromLOS
            ? () =>
                history.push("/los", {
                  iboStack: iboStack.slice(0, -1),
                  showDetailView: iboStack.length - 1 > 1,
                  tabStateByIbo,
                })
            : fromDetails && fromReports && reportStack.length > 0
            ? () => {
                const lastContext = reportStack[reportStack.length - 1];
                if (lastContext) {
                  // console.log(
                  //   "Restoring last report context",
                  //   lastContext,
                  //   reportStack
                  // );
                  history.push("/reports", {
                    ...lastContext,
                    reportStack,
                  });
                } else {
                  // console.log("Stack empty, returning to default reports page");
                  history.push("/reports");
                }
              }
            : undefined
        }
      />

      <div className={styles.tabSwitcherWrapper}>
        <MyInfoTabs
          activeTab={activeTab}
          onTabChange={(tab) => setActiveTab(tab)}
        />
      </div>

      {activeTab === "stats" && (
        <>
          <div className={styles.tabBarWrapper}>
            <TabBar
              onClick={null}
              chartView={true}
              showDivider={false}
              totalLabel={"Businesses"}
              downlineCountsData={details.iboInfo.downlineCounts}
            />
          </div>
          {/* <div className={styles.info}>
            <InfoItem
              description={t("Sponsor:")}
              info={details.iboInfo.loa.sponsorName}
              infoColor={"#91B6BB"}
            />
            <InfoItem
              description={t("Platinum:")}
              info={details.iboInfo.loa.platinumName}
              infoColor={"#91B6BB"}
            />
          </div> */}
          <div className={styles.infoDivider} />
          <div className={styles.date}>
            <DateHeader
              month={month}
              year={year}
              onNext={onNext}
              onBack={onBack}
              yearVisibleOptionsCount={3}
              monthVisibleOptionsCount={12}
              onMonthChange={(newMonth) => {
                setMonth(newMonth);
              }}
              onYearChange={(newYear) => {
                const currentDate = new Date();
                const currentYear = currentDate.getFullYear();
                const currentMonthIndex = currentDate.getMonth();
                const selectedMonthIndex = monthNames.findIndex(
                  (m) => m === month
                );

                if (
                  newYear === currentYear &&
                  selectedMonthIndex > currentMonthIndex
                ) {
                  setMonth(monthNames[currentMonthIndex]);
                }

                setYear(newYear);
              }}
            />
          </div>

          <div className={styles.badgeSectionWrapper}>
            <BadgeSection
              hasBadge={hasBadge}
              pinLevel={pinLevel}
              badgeImageUrl={badgeImageUrl}
              hasNote={hasNote}
              onNoteClick={() => setIsNotesModalOpen(true)}
              onAddNoteClick={() => setIsNotesModalOpen(true)}
            />
          </div>

          <NotesModal
            isOpen={isNotesModalOpen}
            onClose={() => setIsNotesModalOpen(false)}
            date={noteDate}
            headline={noteHeadline}
            body={noteBody}
            author={noteAuthor}
          />

          {/* <div className={styles.itemContainer}>
        {Object.entries(details).map(([sectionKey, sectionData]) => {
          if (sectionKey === "iboInfo" || sectionKey === "majorEvents")
            return null;

          return (
            <div key={sectionKey} className={styles.item}>
              <div className={styles.itemHeader}>
                <h3>{capitalizeWords(sectionKey)}</h3>
                <div>
                  <img src={""} alt="trend" />
                </div>
              </div>

              {Object.entries(sectionData).map(([subKey, valueObj]) => {
                const label = capitalizeWords(subKey);

                // If value is an object (e.g., eagleParameters), render its inner structure
                if (
                  valueObj &&
                  typeof valueObj.value === "undefined" &&
                  typeof valueObj === "object"
                ) {
                  return (
                    <React.Fragment key={subKey}>
                      <div className={styles.itemInfoHeader}>
                        <div>{label}</div>
                      </div>
                      {Object.entries(valueObj).map(
                        ([innerKey, innerValue]) => {
                          if (innerKey === "trend") return null;
                          const trend = valueObj.trend || "same";
                          return (
                            <div key={innerKey} className={styles.itemInfo}>
                              <div>{capitalizeWords(innerKey)}</div>
                              <div>
                                <div style={{ color: trendColors[trend] }}>
                                  {innerValue}
                                </div>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </React.Fragment>
                  );
                }

                // Standard boolean/number value rendering
                const trend = valueObj.trend || "same";
                const displayValue =
                  typeof valueObj.value === "boolean"
                    ? valueObj.value
                      ? "Yes"
                      : "No"
                    : valueObj.value;

                return (
                  <div key={subKey} className={styles.itemInfo}>
                    <div>{label}</div>
                    <div>
                      <div style={{ color: trendColors[trend] }}>
                        {displayValue}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div> */}

          <div className={styles.itemContainer}>
            <div className={styles.item}>
              <div className={styles.itemHeader}>
                <h3>{t("Eagle")}</h3>
                <div>
                  <img src={Eagle} />
                </div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("Qualified Eagle")}</div>
                <div>
                  <div
                    style={{
                      color: trendColors[details.eagle.qualifiedEagle.trend],
                    }}
                  >
                    {details.eagle.qualifiedEagle.value
                      ? t("Yes")
                      : details.eagle.qualifiedEagle.value === false
                      ? t("No")
                      : "N/A"}
                  </div>
                </div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("Qualified Double Eagle")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[details.eagle.qualifiedDoubleEagle.trend],
                    }}
                  >
                    {details.eagle.qualifiedDoubleEagle.value
                      ? t("Yes")
                      : details.eagle.qualifiedDoubleEagle.value === false
                      ? t("No")
                      : "N/A"}
                  </div>
                </div>
              </div>

              <div className={styles.itemInfo}>
                <div>{t("Eagle Parameters")}</div>
                <div>
                  <span
                    style={{
                      color: trendColors[details.eagle.totalBonus.trend],
                    }}
                  >
                    {details.eagle.totalBonus.value ?? "N/A"}-
                  </span>

                  <span
                    style={{
                      color: trendColors[details.eagle.totalPremier.trend],
                    }}
                  >
                    {details.eagle.totalPremier.value ?? "N/A"}
                  </span>

                  <span
                    style={{
                      color:
                        trendColors[details.eagle.totalMajorFunctions.trend],
                    }}
                  >
                    -{details.eagle.totalMajorFunctions.value ?? "N/A"}
                  </span>
                </div>
              </div>
            </div>

            <div className={styles.item}>
              <div className={styles.itemHeader}>
                <h3>{t("Product Volume")}</h3>
                <div>
                  <img
                    src={
                      details.productVolume.trend == "up"
                        ? TrendUp
                        : details.productVolume.trend == "same"
                        ? TrendFlat
                        : details.productVolume.trend == "down"
                        ? TrendDown
                        : ""
                    }
                  />
                </div>
              </div>
              <div className={styles.itemInfoHeader}>
                <div>{t("Personal")}</div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("Personal Use PV")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[details.productVolume.personalUsePV.trend],
                    }}
                  >
                    {details.productVolume.personalUsePV.value ?? "N/A"}
                  </div>
                </div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("Retail (VCS)")}</div>
                <div>
                  <div
                    style={{
                      color: trendColors[details.productVolume.retailVCS.trend],
                    }}
                  >
                    {details.productVolume.retailVCS.value ?? "N/A"}
                  </div>
                </div>
              </div>

              <div className={styles.itemInfo}>
                <div>{t("Personal VCS")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[
                          details.productVolume.personalVCSPercent.trend
                        ],
                    }}
                  >
                    {details.productVolume.personalVCSPercent.value ?? "N/A"}
                    {details.productVolume.personalVCSPercent.value !== null &&
                      "%"}
                  </div>
                </div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("First Circle Total (P + R)")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[
                          details.productVolume.firstCircleTotal.trend
                        ],
                    }}
                  >
                    {details.productVolume.firstCircleTotal.value ?? "N/A"}
                  </div>
                </div>
              </div>

              <div className={styles.itemInfo}>
                <div>{t("Customer Orders")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[details.productVolume.customerOrders.trend],
                    }}
                  >
                    {details.productVolume.customerOrders.value ?? "N/A"}
                  </div>
                </div>
              </div>

              <div className={styles.itemInfoHeader}>
                <div>{t("Group")}</div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("Total Group PV")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[details.productVolume.totalGroupPV.trend],
                    }}
                  >
                    {details.productVolume.totalGroupPV.value ?? "N/A"}
                  </div>
                </div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("Total Group VCS")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[
                          details.productVolume.totalGroupVCSPercent.trend
                        ],
                    }}
                  >
                    {details.productVolume.totalGroupVCSPercent.value ?? "N/A"}
                    {details.productVolume.totalGroupVCSPercent.value && "%"}
                  </div>
                </div>
              </div>

              <div className={styles.itemInfo}>
                <div>{t("Group Non-bonusable BV")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[
                          details.productVolume.groupNonBonusableBV.trend
                        ],
                    }}
                  >
                    {details.productVolume.groupNonBonusableBV.value ?? "N/A"}
                  </div>
                </div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("PV Per Business")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[details.productVolume.pvPerBusiness.trend],
                    }}
                  >
                    {details.productVolume.pvPerBusiness.value ?? "N/A"}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.item}>
              <div className={styles.itemHeader}>
                <h3>{t("Business Growth")}</h3>
                <div>
                  <img
                    src={
                      details.businessGrowth.trend == "up"
                        ? TrendUp
                        : details.businessGrowth.trend == "same"
                        ? TrendFlat
                        : details.businessGrowth.trend == "down"
                        ? TrendDown
                        : ""
                    }
                  />
                </div>
              </div>

              <div className={styles.itemInfo}>
                <div>{t("Total Number of Businesses")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[
                          details.businessGrowth.totalBusinesses.trend
                        ],
                    }}
                  >
                    {details.businessGrowth.totalBusinesses.value ?? "N/A"}
                  </div>
                </div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("Legs Receiving a Bonus Check")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[details.businessGrowth.bonusLegs.trend],
                    }}
                  >
                    {details.businessGrowth.bonusLegs.value ?? "N/A"}
                  </div>
                </div>
              </div>

              <div className={styles.itemInfo}>
                <div>{t("First-Time Meet & Greets")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[
                          details.businessGrowth.firstTimeMeetGreets.trend
                        ],
                    }}
                  >
                    {details.businessGrowth.firstTimeMeetGreets.value ?? "N/A"}
                  </div>
                </div>
              </div>

              <div className={styles.itemInfo}>
                <div>{t("Times You Showed the Plan")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[details.businessGrowth.plansShown.trend],
                    }}
                  >
                    {details.businessGrowth.plansShown.value ?? "N/A"}
                  </div>
                </div>
              </div>

              <div className={styles.itemInfo}>
                <div>{t("New IBOs Registered Personally in Width")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[
                          details.businessGrowth.newPersonalIBOs.trend
                        ],
                    }}
                  >
                    {details.businessGrowth.newPersonalIBOs.value ?? "N/A"}
                  </div>
                </div>
              </div>

              <div className={styles.itemInfo}>
                <div>{t("Downline Showing Their Own Plans")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[
                          details.businessGrowth.downlinePlansShown.trend
                        ],
                    }}
                  >
                    {details.businessGrowth.downlinePlansShown.value ?? "N/A"}
                  </div>
                </div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("Eagles")}</div>
                <div>
                  <div
                    style={{
                      color: trendColors[details.businessGrowth.eagles.trend],
                    }}
                  >
                    {details.businessGrowth.eagles.value ?? "N/A"}
                  </div>
                </div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("Double Eagles")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[details.businessGrowth.doubleEagles.trend],
                    }}
                  >
                    {details.businessGrowth.doubleEagles.value ?? "N/A"}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.item}>
              <div className={styles.itemHeader}>
                <h3>{t("Premier Membership")}</h3>
                <div>
                  <img
                    src={
                      details.premierMembership.trend == "up"
                        ? TrendUp
                        : details.premierMembership.trend == "same"
                        ? TrendFlat
                        : details.premierMembership.trend == "down"
                        ? TrendDown
                        : ""
                    }
                  />
                </div>
              </div>

              <div className={styles.itemInfo}>
                <div>{t("Personal Premier Members")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[details.premierMembership.personal.trend],
                    }}
                  >
                    {details.premierMembership.personal.value ?? "N/A"}
                  </div>
                </div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("Total Premier Members")}</div>
                <div>
                  <div
                    style={{
                      color: trendColors[details.premierMembership.total.trend],
                    }}
                  >
                    {details.premierMembership.total.value ?? "N/A"}
                  </div>
                </div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("Personal Digital Delivery")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[
                          details.premierMembership.personalDigitalDelivery
                            .trend
                        ],
                    }}
                  >
                    {details.premierMembership.personalDigitalDelivery.value ??
                      "N/A"}
                  </div>
                </div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("Total Digital Delivery")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[
                          details.premierMembership.totalDigitalDelivery.trend
                        ],
                    }}
                  >
                    {details.premierMembership.totalDigitalDelivery.value ??
                      "N/A"}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.item}>
              <div className={styles.itemHeader}>
                <h3>DreamStream®</h3>
                <div>
                  <img
                    src={
                      details.dreamStream.trend == "up"
                        ? TrendUp
                        : details.dreamStream.trend == "same"
                        ? TrendFlat
                        : details.dreamStream.trend == "down"
                        ? TrendDown
                        : ""
                    }
                  />
                </div>
              </div>

              <div className={styles.itemInfo}>
                <div>{t("Posts")}</div>
                <div>
                  <div
                    style={{
                      color: trendColors[details.dreamStream.posts.trend],
                    }}
                  >
                    {details.dreamStream.posts.value ?? "N/A"}
                  </div>
                </div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("Comments")}</div>
                <div>
                  <div
                    style={{
                      color: trendColors[details.dreamStream.comments.trend],
                    }}
                  >
                    {details.dreamStream.comments.value ?? "N/A"}
                  </div>
                </div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("Reactions")}</div>
                <div>
                  <div
                    style={{
                      color: trendColors[details.dreamStream.reactions.trend],
                    }}
                  >
                    {details.dreamStream.reactions.value ?? "N/A"}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.item}>
              <div className={styles.itemHeader}>
                <h3>Topics</h3>
                <div>
                  <img
                    src={
                      details.topics.trend == "up"
                        ? TrendUp
                        : details.topics.trend == "same"
                        ? TrendFlat
                        : details.topics.trend == "down"
                        ? TrendDown
                        : ""
                    }
                  />
                </div>
              </div>

              <div className={styles.itemInfo}>
                <div>{t("Topics Listened To")}</div>
                <div>
                  <div
                    style={{
                      color: trendColors[details.topics.listenedTo.trend],
                    }}
                  >
                    {details.topics.listenedTo.value ?? "N/A"}
                  </div>
                </div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("Topics Sent")}</div>
                <div>
                  <div
                    style={{
                      color: trendColors[details.topics.sent.trend],
                    }}
                  >
                    {details.topics.sent.value ?? "N/A"}
                  </div>
                </div>
              </div>
              <div className={styles.itemInfo}>
                <div>Threads</div>
                <div>
                  <div
                    style={{
                      color: trendColors[details.topics.threadsSent.trend],
                    }}
                  >
                    {details.topics.threadsSent.value ?? "N/A"}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.item}>
              <div className={styles.itemHeader}>
                <h3>{t("Local Events")}</h3>
                <div>
                  <img
                    src={
                      details.localEvents.trend == "up"
                        ? TrendUp
                        : details.localEvents.trend == "same"
                        ? TrendFlat
                        : details.localEvents.trend == "down"
                        ? TrendDown
                        : ""
                    }
                  />
                </div>
              </div>

              <div className={styles.itemInfo}>
                <div>{t("Second Look")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[
                          details.localEvents.secondLookMeeting.trend
                        ],
                    }}
                  >
                    {details.localEvents.secondLookMeeting.value ?? "N/A"}
                  </div>
                </div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("Regional")}</div>
                <div>
                  <div
                    style={{
                      color: trendColors[details.localEvents.regional.trend],
                    }}
                  >
                    {details.localEvents.regional.value ?? "N/A"}
                  </div>
                </div>
              </div>
              <div className={styles.itemInfo}>
                <div>{t("Organizational")}</div>
                <div>
                  <div
                    style={{
                      color:
                        trendColors[details.localEvents.organizational.trend],
                    }}
                  >
                    {details.localEvents.organizational.value ?? "N/A"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <EventTicketsInfoList
            majorEventsData={details.majorEvents}
            header={t("Major Events (yoy)")}
          />
        </>
      )}

      {activeTab === "nameList" && (
        <>
          <div className={styles.nameListHeaderSpacer} />
          <div className={styles.date}>
            <DateHeader
              month={month}
              year={year}
              onNext={onNext}
              onBack={onBack}
              yearVisibleOptionsCount={3}
              monthVisibleOptionsCount={12}
              onMonthChange={(newMonth) => {
                setMonth(newMonth);
              }}
              onYearChange={(newYear) => {
                const currentDate = new Date();
                const currentYear = currentDate.getFullYear();
                const currentMonthIndex = currentDate.getMonth();
                const selectedMonthIndex = monthNames.findIndex(
                  (m) => m === month
                );

                if (
                  newYear === currentYear &&
                  selectedMonthIndex > currentMonthIndex
                ) {
                  setMonth(monthNames[currentMonthIndex]);
                }

                setYear(newYear);
              }}
            />
          </div>

          <NameListTab
            prospects={prospects}
            customers={customers}
            onPinClick={() => {}}
            onItemClick={() => {}}
          />
        </>
      )}

      {activeTab === "info" && (
        <>
          <div className={styles.infoTabHeaderSpacer} />
          <InfoTab
            sponsor={sponsor}
            platinum={platinum}
            members={members}
            onSponsorClick={() => {}}
            onPlatinumClick={() => {}}
          />
        </>
      )}
    </div>
  );
};
